import * as React from 'react'

import AvatarImage from 'components/atoms/AvatarImage'
import Typography from 'components/atoms/_layouts/Typography'
import styled from 'styled-components'
import dompurify from 'dompurify'
import I18n from 'core/i18n'
import moment from 'moment'
import * as constants from '../../static/constants'

type IMessageGroup = 'with_agent' | 'with_guide' | 'with_members' | 'with_guide_assign'
interface IProps {
  user: any
  conversation: any
  type?: IMessageGroup
}

const purifyBody = body => {
  const purified_body = dompurify.sanitize(body, {
    ALLOWED_TAGS: ['a'],
    ALLOWED_ATTR: ['href', 'target'],
  })
  return purified_body
}
const getConversationGroupName = (user: any, type: IMessageGroup) => {
  if (type === 'with_members') {
    return I18n.t('conversation.messages_with_members')
  }
  const withAgent = (type === 'with_agent' || type === 'with_guide_assign') && !user.agent
  const withGuide =
    (type === 'with_guide' && !user.guide) || (type === 'with_guide_assign' && user.agent)

  if (withAgent) {
    return I18n.t('conversation.messages_with_agent')
  }
  if (withGuide) {
    return I18n.t('conversation.messages_with_guide')
  }
  return I18n.t('conversation.messages_with_guests')
}

const MessageLink: React.VFC<IProps> = ({ user, conversation, type }) => {
  const latestMessage = conversation.latest_message
  const formatSentAtDate = date => {
    const initDate =
      I18n.locale === 'en' ? moment(date) : moment(date, 'YYYY年MM月DD日 HH時mm分3ss ')

    return initDate.format('YYYY-MM-DD HH:mm')
  }

  return (
    <MessageLinkWrapper href={`/conversations/${conversation.slug || conversation.id}`}>
      <div className="Avatar">
        <AvatarImage avatarURL={latestMessage.sender?.avatar_url} size={60} />
        <div className="Avatar_Info">
          {type && <span>{getConversationGroupName(user, type)}</span>}
          {latestMessage.read_count > 0 && <span className="newMessage">NEW</span>}

          <Typography type="subtitle" size="xl">
            {latestMessage.sender?.username}
          </Typography>
        </div>
      </div>
      <div className="Message_Info">
        <Typography type="body" size="xl">
          <div
            className="message_body"
            dangerouslySetInnerHTML={{ __html: purifyBody(latestMessage.body) }}
          />
        </Typography>
        <span className="sent_at">{formatSentAtDate(latestMessage.sent_at)}</span>
      </div>

      {latestMessage.file_attached && (
        <div className="Message_image">
          <img src={latestMessage.file_url} height="80" loading="lazy" />
        </div>
      )}
    </MessageLinkWrapper>
  )
}

const MessageLinkWrapper = styled.a`
  display: flex;
  padding: 16px;
  width: 100%;
  background-color: ${constants.COLORS.background};
  border-radius: 10px;
  gap: 8px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  &:hover {
    opacity: 0.8;
  }
  & + & {
    margin-top: 8px;
  }
  .Avatar {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .Avatar_Info {
    flex: 1;
    min-width: 220px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      min-width: 160px;
    }

    span {
    font-size: 0.8em;
    color: ${constants.COLORS.subText};
    }
    .newMessage {
      background-color: ${constants.COLORS.info};
      color: white;
      border-radius: 4px;
      padding: 2px 4px;
      margin-left: 4px;
    }
  }

  .Message_Info {
    flex: 2;
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: space-between;
    min-width: 0;
    .message_body {
      padding: 8px 16px;
      border-radius: 12px;
      border: solid 1px ${constants.COLORS.divider};
      background-color: ${constants.COLORS.white}
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sent_at {
      font-size: 12px;
      margin-left: auto;
      color: ${constants.COLORS.subText};
    }
  }

  .Message_image {
    margin-right: 2rem;
    height: 80px;
    img {
      border-radius: 5px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        height: 38px;
      }
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      margin-right: 0.2rem;
      height: 38px;
    }
  }

  .Message_Link {
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }
  }
`

export default MessageLink
